import {Component, Input, OnChanges, OnDestroy, OnInit} from '@angular/core';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {ResourceService} from '../../service-data/resource.service';
import {Resource} from '../../../model/Resource';
import {LanguagePipe} from '../../pipe/language.pipe';
import {
  bestAvailableStage,
  isPreliminary,
  PdfArtifactStatus,
  PdfImageStage,
  PdfProcessingNotice,
  processingNotice
} from '../../service-ui/pdf-artifact-stage';

/**
 * "The image you are looking at is not the final one."
 *
 * ── Why this exists ──────────────────────────────────────────────────────
 * A page image can come from any of three artefacts — the customer's
 * original upload, the PREPAREd file, or the STANDARDIZEd file
 * (`docs/architecture/pdf-pipeline.md`). They can look materially different:
 * an original may still have unflattened layers, substituted fonts, and
 * non-printing objects that will not appear in print.
 *
 * The server picks which one to serve and does not say which it picked
 * (`docs/contracts/shared/Notifications.md` §9). So until today the UI
 * presented an unverified render exactly as it presents a verified one, with
 * nothing to distinguish them. This component is the distinction.
 *
 * ── Deliberately small ───────────────────────────────────────────────────
 * The brief was "not overly long — could just be an icon with a value that
 * explains when hovered". So: one icon, hover text carrying the detail, and
 * an optional one-word label where there is room. **It renders nothing at
 * all once the final stage is reached**, which is the common case — a
 * finished book shows no extra ink, exactly like the resource-id badge's
 * "invisible when done" stripe (`service-ui/resource-status.ts`).
 *
 * ── Why `prepared` is looked up but `standardized` is bound ──────────────
 * `DraftCover` and `DraftBody` carry only `resource_ID`, not a `resource`
 * object, so there is nothing for a caller to bind — the Resource has to be
 * resolved from the cache here. And because that resolution is a method call
 * rather than a binding, a change to `Resource.prepared` cannot fire
 * `ngOnChanges`; hence the `updateNotifier` subscription.
 *
 * `standardized`, by contrast, lives directly on the `Cover`/`Body` the
 * caller already holds, so it is bound as a **primitive string**. Binding the
 * primitive rather than the object is what makes `ngOnChanges` work here when
 * it does not work for the image components: the string's identity changes
 * when the value does, whereas `addUpdateLocalItem` mutates objects in place
 * and leaves their identity alone.
 */
@Component({
  selector: 'app-preview-quality-indicator',
  templateUrl: './preview-quality-indicator.component.html',
  styleUrls: ['./preview-quality-indicator.component.css']
})
export class PreviewQualityIndicatorComponent implements OnInit, OnChanges, OnDestroy {

  /** The Resource backing the image being shown. 0 / absent = nothing uploaded. */
  @Input() resourceId: number;

  /**
   * `Cover.standardized` / `Body.standardized`.
   *
   * **Leave unbound for Drafts.** `undefined` is how this component learns
   * that the artefact has no standardize stage and that a PREPARED render is
   * therefore final. Binding `''` (which is what `new Cover(...)` starts at)
   * means the opposite: a Book whose status has not arrived yet.
   */
  @Input() standardized: string;

  /**
   * Force the "does this standardize?" answer.
   *
   * Bind `true` on Book surfaces that may render before the `Cover`/`Body`
   * row has loaded — otherwise a Book is briefly mistaken for a Draft and a
   * PREPARED render is briefly labelled final, which is a flicker that says
   * the wrong thing.
   */
  @Input() standardizes: boolean;

  /** Icon only, no label. For thumbnails and other tight slots. */
  @Input() compact = false;

  notice: PdfProcessingNotice = 'NONE';
  stage: PdfImageStage = 'NONE';
  preliminary = false;
  icon = '';
  label = '';
  hoverText = '';

  private onDestroySubject: Subject<boolean> = new Subject<boolean>();

  constructor(private resourceService: ResourceService) {
  }

  ngOnInit() {
    this.recompute();

    // `Resource.prepared` is read through a cache lookup, not a binding, so
    // nothing would otherwise tell this component that preparation finished.
    this.resourceService.updateNotifier
      .pipe(takeUntil(this.onDestroySubject))
      .subscribe((resource: Resource) => {
        if (resource && resource.getId && resource.getId() === this.resourceId) {
          this.recompute();
        }
      });
  }

  ngOnChanges() {
    this.recompute();
  }

  ngOnDestroy() {
    this.onDestroySubject.next(true);
    this.onDestroySubject.complete();
  }

  /** Everything this component knows, in the shape the stage rules want. */
  private status(): PdfArtifactStatus {
    const resource = this.resourceId ? this.resourceService.getLocalItem(this.resourceId) : null;
    return {
      resourceId: this.resourceId,
      prepared: resource ? resource.prepared : undefined,
      standardized: this.standardized,
      standardizes: this.standardizes
    };
  }

  private recompute(): void {
    const status = this.status();
    this.stage = bestAvailableStage(status);
    this.notice = processingNotice(status);
    this.preliminary = isPreliminary(status);

    if (this.notice === 'NONE') {
      this.icon = '';
      this.label = '';
      this.hoverText = '';
      return;
    }

    if (this.notice === 'ERROR') {
      this.icon = 'error_outline';
      this.label = LanguagePipe.get('Error', 'compressSpaces');
      this.hoverText = LanguagePipe.get('PreviewProcessingErrorHint', 'compressSpaces');
      return;
    }

    this.icon = 'hourglass_top';
    this.label = LanguagePipe.get('PreliminaryPreviewLabel', 'compressSpaces');
    this.hoverText = this.notice === 'PREPARING'
      ? LanguagePipe.get('PreliminaryPreviewPreparingHint', 'compressSpaces')
      : LanguagePipe.get('PreliminaryPreviewStandardizingHint', 'compressSpaces');
  }

  /** Nothing to say once the artefact has reached its final stage. */
  get visible(): boolean {
    return this.notice !== 'NONE';
  }
}

